(() => {
  'use strict';

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class DrawerController {
    constructor() {
      this.activeDrawer = null;
      this.trigger = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.bind();
    }

    bind() {
      document.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-drawer-open]');
        if (openButton) {
          event.preventDefault();
          this.open(openButton.dataset.drawerOpen, openButton);
          return;
        }

        const closeButton = event.target.closest('[data-drawer-close]');
        if (closeButton) {
          event.preventDefault();
          this.close(closeButton.closest('[data-drawer]') || this.activeDrawer);
        }
      });

      window.addEventListener('pageshow', () => this.closeAll(false));
    }

    open(id, trigger = null) {
      const drawer = typeof id === 'string' ? document.getElementById(id) : id;
      if (!drawer) return;

      if (this.activeDrawer && (!this.activeDrawer.isConnected || this.activeDrawer !== drawer)) {
        this.closeAll(false);
      }

      this.activeDrawer = drawer;
      this.trigger = trigger || document.querySelector(`[data-drawer-open="${drawer.id}"]`);
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      document.querySelectorAll(`[data-drawer-open="${drawer.id}"]`).forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });

      document.addEventListener('keydown', this.boundKeydown);
      window.setTimeout(() => {
        const panel = drawer.querySelector('.drawer__panel');
        const firstFocusable = panel?.querySelector(focusableSelector);
        (firstFocusable || panel)?.focus({ preventScroll: true });
      }, 80);
    }

    close(drawer = this.activeDrawer, restoreFocus = true) {
      if (!drawer) {
        this.closeAll(false);
        return;
      }

      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.querySelectorAll(`[data-drawer-open="${drawer.id}"]`).forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      if (drawer === this.activeDrawer || !this.activeDrawer?.isConnected) {
        this.activeDrawer = null;
        document.body.classList.remove('drawer-open');
        document.removeEventListener('keydown', this.boundKeydown);
        if (restoreFocus && this.trigger instanceof HTMLElement) {
          this.trigger.focus({ preventScroll: true });
        }
        this.trigger = null;
      }

      if (!document.querySelector('[data-drawer].is-open')) {
        document.body.classList.remove('drawer-open');
      }
    }

    closeAll(restoreFocus = false) {
      const trigger = this.trigger;
      document.querySelectorAll('[data-drawer]').forEach((drawer) => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
      });
      document.querySelectorAll('[data-drawer-open]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      this.activeDrawer = null;
      this.trigger = null;
      document.body.classList.remove('drawer-open');
      document.removeEventListener('keydown', this.boundKeydown);

      if (restoreFocus && trigger instanceof HTMLElement && trigger.isConnected) {
        trigger.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (!this.activeDrawer) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const panel = this.activeDrawer.querySelector('.drawer__panel');
      const focusable = [...panel.querySelectorAll(focusableSelector)].filter(
        (element) => element.offsetParent !== null
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const releasePage = () => {
    window.NNLDrawers?.closeAll(false);
    document.documentElement.classList.add('nnl-intro-skip', 'nnl-page-released');
    document.documentElement.style.removeProperty('overflow');
    document.body.classList.remove('drawer-open', 'intro-active');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');

    document.querySelectorAll('[data-drawer]').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('[data-drawer-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    const intro = document.querySelector('[data-intro]');
    if (intro) {
      intro.setAttribute('aria-hidden', 'true');
      intro.remove();
    }

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement.closest('[data-drawer], [data-intro]')) {
      activeElement.blur();
    }
  };

  const initIntro = () => {
    const intro = document.querySelector('[data-intro]');
    if (!intro || document.documentElement.classList.contains('nnl-intro-skip')) {
      document.body.classList.remove('intro-active');
      return;
    }

    document.body.classList.add('intro-active');
    intro.focus({ preventScroll: true });
    const duration = Number.parseInt(intro.dataset.duration, 10) || 1500;
    let finished = false;
    let timer;

    const finish = () => {
      if (finished) return;
      finished = true;
      try {
        sessionStorage.setItem('nnl-intro-seen', '1');
      } catch (error) {
        // Brak dostępu do pamięci przeglądarki nie może zablokować strony.
      }
      intro.classList.add('is-leaving');
      document.body.classList.remove('intro-active');
      document.removeEventListener('keydown', onIntroKeydown);
      window.setTimeout(() => intro.remove(), 320);
    };

    const onIntroKeydown = (event) => {
      if (event.key === 'Tab') return;
      window.clearTimeout(timer);
      finish();
    };

    timer = window.setTimeout(finish, duration);
    intro.addEventListener('click', () => {
      window.clearTimeout(timer);
      finish();
    });
    document.addEventListener('keydown', onIntroKeydown);
  };

  const initHeader = () => {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;

    let queued = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
      queued = false;
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  const initProductGallery = (scope = document) => {
    scope.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryReady) return;
      gallery.dataset.galleryReady = 'true';

      gallery.addEventListener('click', (event) => {
        const button = event.target.closest('[data-media-target]');
        if (!button) return;
        const target = document.getElementById(button.dataset.mediaTarget);
        if (!target) return;
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start'
        });
        gallery.querySelectorAll('[data-media-target]').forEach((thumb) => {
          const active = thumb === button;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', String(active));
        });
      });
    });
  };

  const initRecommendations = (scope = document) => {
    scope.querySelectorAll('[data-product-recommendations][data-url]').forEach((section) => {
      if (section.dataset.recommendationsReady) return;
      section.dataset.recommendationsReady = 'true';

      const load = () => {
        fetch(section.dataset.url)
          .then((response) => {
            if (!response.ok) throw new Error('Recommendations request failed');
            return response.text();
          })
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const replacement = html.querySelector('[data-product-recommendations]');
            if (!replacement || !replacement.innerHTML.trim()) {
              section.remove();
              return;
            }
            section.innerHTML = replacement.innerHTML;
          })
          .catch(() => {
            section.remove();
          });
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            load();
          },
          { rootMargin: '0px 0px 300px 0px' }
        );
        observer.observe(section);
      } else {
        load();
      }
    });
  };

  const initDetailsControls = () => {
    document.addEventListener('click', (event) => {
      const closeButton = event.target.closest('[data-details-close]');
      if (closeButton) closeButton.closest('details')?.removeAttribute('open');
    });
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-toast]');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 3000);
  };

  const initialize = (scope = document) => {
    initProductGallery(scope);
    initRecommendations(scope);
  };

  document.addEventListener('DOMContentLoaded', () => {
    window.NNLDrawers = new DrawerController();
    window.NNLShowToast = showToast;
    window.NNLInitialize = initialize;
    window.NNLReleasePage = releasePage;

    initIntro();
    initHeader();
    initialize();
    initDetailsControls();
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) releasePage();
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
